import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AddCircleOutline as RequestIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as PendingIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../animations/variants';
import { getMyRequests, getRequest } from '../../services/jitApi';

const JITMyRequests = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery('(max-width:767px)');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25, total: 0, totalPages: 0 });

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getMyRequests({ page: pagination.page + 1, pageSize: pagination.pageSize });
      setRequests(response.data?.requests || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data?.total || 0,
        totalPages: response.data?.totalPages || 0,
      }));
    } catch (err) {
      console.error('Failed to fetch requests:', err);
      setError('Failed to load your requests');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleViewDetails = async (requestId) => {
    try {
      const response = await getRequest(requestId);
      setSelectedRequest(response.data);
      setDetailsOpen(true);
    } catch (err) {
      console.error('Failed to fetch request details:', err);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', icon: <PendingIcon sx={{ fontSize: 14 }} />, label: 'Pending' },
      approved: { color: 'success', icon: <ApprovedIcon sx={{ fontSize: 14 }} />, label: 'Approved' },
      rejected: { color: 'error', icon: <RejectedIcon sx={{ fontSize: 14 }} />, label: 'Rejected' },
      expired: { color: 'default', icon: <TimeIcon sx={{ fontSize: 14 }} />, label: 'Expired' },
      revoked: { color: 'error', icon: <RejectedIcon sx={{ fontSize: 14 }} />, label: 'Revoked' },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        size="small"
        label={config.label}
        color={config.color}
        icon={config.icon}
        sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#4698D3' }} />
      </Box>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <motion.div variants={sectionVariants}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: { xs: 2, sm: 0 }, mb: { xs: 3, sm: 4 } }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Bitter, Georgia, serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1,
            }}
          >
            My Requests
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
            }}
          >
            View and track all your access requests
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={fetchRequests}
              sx={{
                border: '1px solid #E5E5E5',
                borderRadius: '6px',
                '&:hover': {
                  borderColor: '#4698D3',
                  backgroundColor: 'rgba(70, 152, 211, 0.04)',
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<RequestIcon />}
            onClick={() => navigate('/request')}
            sx={{
              backgroundColor: '#4698D3',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#3580B8',
              },
            }}
          >
            New Request
          </Button>
        </Box>
      </Box>

      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <motion.div variants={sectionVariants}>
      {requests.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'rgba(70, 152, 211, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 2,
            }}
          >
            <RequestIcon sx={{ fontSize: 32, color: '#4698D3' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1,
            }}
          >
            No Requests Yet
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
              mb: 3,
            }}
          >
            You haven't made any access requests yet
          </Typography>
          <Button
            variant="contained"
            startIcon={<RequestIcon />}
            onClick={() => navigate('/request')}
            sx={{
              backgroundColor: '#4698D3',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#3580B8',
              },
            }}
          >
            Request Access
          </Button>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {requests.map((request, index) => (
            <Paper
              key={request.id}
              elevation={0}
              onClick={() => handleViewDetails(request.id)}
              sx={{
                '@keyframes fadeInItem': {
                  from: { opacity: 0, transform: 'translateY(8px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                p: 2,
                border: '1px solid #E5E5E5',
                borderRadius: '8px',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#4698D3',
                  backgroundColor: 'rgba(70, 152, 211, 0.02)',
                },
                '&:active': {
                  backgroundColor: 'rgba(70, 152, 211, 0.06)',
                },
              }}
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  color: '#253E66',
                  mb: 1.5,
                }}
              >
                {request.accountName || request.accountId}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', flexShrink: 0 }}
                >
                  Permission Set
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                    color: '#333333',
                    ml: 2,
                    textAlign: 'right',
                  }}
                >
                  {request.permissionSetName || request.permissionSetId}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', flexShrink: 0 }}
                >
                  Requested
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    color: '#333333',
                    ml: 2,
                    textAlign: 'right',
                  }}
                >
                  {formatDate(request.createdAt)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pt: 1.5,
                  borderTop: '1px solid #F0F0F0',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  {getStatusChip(request.status)}
                  <Chip
                    size="small"
                    icon={<TimeIcon sx={{ fontSize: 14 }} />}
                    label={`${request.requestedDuration}h`}
                    sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}
                  />
                </Box>
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{ display: 'flex', gap: 0.5 }}
                >
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => handleViewDetails(request.id)}
                      sx={{
                        color: '#4698D3',
                        '&:hover': {
                          backgroundColor: 'rgba(70, 152, 211, 0.08)',
                        },
                      }}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          ))}
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={(e, newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={(e) => setPagination((prev) => ({ ...prev, page: 0, pageSize: parseInt(e.target.value, 10) }))}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #E5E5E5',
              fontFamily: 'Metropolis, sans-serif',
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontFamily: 'Metropolis, sans-serif',
              },
            }}
          />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            overflowX: 'auto',
          }}
        >
          <Table sx={{ minWidth: { xs: 450, sm: 650 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Account
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Permission Set
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Duration
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Requested
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Status
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request, index) => (
                <TableRow
                  key={request.id}
                  hover
                  sx={{
                    '@keyframes fadeInRow': {
                      from: { opacity: 0, transform: 'translateY(8px)' },
                      to: { opacity: 1, transform: 'translateY(0)' },
                    },
                    animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                    '&:hover': {
                      backgroundColor: 'rgba(70, 152, 211, 0.02)',
                    },
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        color: '#253E66',
                      }}
                    >
                      {request.accountName || request.accountId}
                    </Typography>
                    {request.accountName && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666666',
                          fontFamily: 'Metropolis, sans-serif',
                        }}
                      >
                        {request.accountId}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        color: '#333333',
                      }}
                    >
                      {request.permissionSetName || request.permissionSetId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        color: '#333333',
                      }}
                    >
                      {request.requestedDuration}h
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        color: '#666666',
                      }}
                    >
                      {formatDate(request.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(request.status)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(request.id)}
                        sx={{
                          color: '#4698D3',
                          '&:hover': {
                            backgroundColor: 'rgba(70, 152, 211, 0.08)',
                          },
                        }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={(e, newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={(e) => setPagination((prev) => ({ ...prev, page: 0, pageSize: parseInt(e.target.value, 10) }))}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #E5E5E5',
              fontFamily: 'Metropolis, sans-serif',
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontFamily: 'Metropolis, sans-serif',
              },
            }}
          />
        </TableContainer>
      )}
      </motion.div>

      {/* Request Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
          Request Details
        </DialogTitle>
        <DialogContent dividers>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          {selectedRequest && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {getStatusChip(selectedRequest.status)}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  AWS Account
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                  {selectedRequest.accountName || selectedRequest.accountId}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  Permission Set
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                  {selectedRequest.permissionSetName || selectedRequest.permissionSetId}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  Duration
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                  {selectedRequest.requestedDuration} hours
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  Justification
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  {selectedRequest.justification}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  Requested At
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  {formatDate(selectedRequest.createdAt)}
                </Typography>
              </Box>
              {selectedRequest.reviewerEmail && (
                <>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                      Reviewed By
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                      {selectedRequest.reviewerEmail}
                    </Typography>
                  </Box>
                  {selectedRequest.reviewedAt && (
                    <Box>
                      <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        Reviewed At
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        {formatDate(selectedRequest.reviewedAt)}
                      </Typography>
                    </Box>
                  )}
                  {selectedRequest.reviewerComment && (
                    <Box>
                      <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        Reviewer Comment
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                        {selectedRequest.reviewerComment}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
          </motion.div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetailsOpen(false)}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              textTransform: 'none',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default JITMyRequests;
